import { BusinessAccountType } from '@lib/formSchemas/businessAccountTypeSelection/types'

export type Response<T> = {
  data: T
  meta: {
    code: number
    message: string
    errorDetail: string
  }
}

export type AddressInfo = {
  city: string
  state: string
  zip: string
  country: string
  address_1: string
  address_2: string
}

export type Address = {
  city: string
  state: string
  zip: string
  country: string
  first_name: string
  middle_name: string
  last_name: string
  care_of: string
  address_1: string
  address_2: string
}

export type KycStatus = {
  message: string
  status: string
  passed: boolean
  needs_review: boolean
  account_queued: boolean
  attempted: boolean
  under_age: boolean
  has_existing_account: boolean
  address: Address
  verified_address: Address
  inquiry_id: boolean
  birthdate: boolean
  minor_accounts_allowed: boolean
  limited_account: boolean
  paycard_account: boolean
  allow_additional_documentation: boolean
  additional_documentation_attempted: boolean
}

export type KycStatusResponse = Response<KycStatus>

export type CreateWalletRequest = {
  create_physical_card?: boolean
  has_existing_account?: boolean
  terms: Terms[]
  address: AddressInfo
  birthdate?: string
  account_type?: ACCOUNT_TYPE
  version?: number
  claimed_account_id?: string
  business_type?: BusinessAccountType
}

export type AttachInstantCardRequest = {
  name_on_card: string
  last_four: string
}

export type CreateWallet = {
  app: string
  created_time: string
  email: string
  id: number
  last_modified_time: string
  login_link: string
  marqeta_user_token: string
  cards: Card[]
  balance: Balance
  minor: boolean
  status: string
  wallet_state: string
  billing_address: BillingAddress
  type: ACCOUNT_TYPE
  ach?: Ach
  bank_info?: {
    name: string
  }
}

export type CreateWalletResponse = Response<CreateWallet>

export type AppIdentifier =
  | 'BRANCH'
  | 'UBER_PRO_CARD'
  | 'NURSA'
  | 'PAYMINT'
  | 'SHIFTKEY_WALLET'
  | 'USHG_WALLET'
  | 'INSTACART_CARD'

export type OrgDetails = {
  app_identifier?: AppIdentifier
  app_display_name?: string
  appName?: string
  auto_join_default_location: true
  default_location_id: string
  login_deep_link: string
  id: number
  logo_url: string | null
  idp_sso_url: string | null
  name: string
  org_key: string
  organization_parent_id?: number | null
  branding_color_hex_code: string | null
  require_roster_match_before_kyc: boolean
}

export type OrgDetailsResponse = Response<OrgDetails>

export type LandingSlide = {
  imageUrl?: string
  CTA?: string
  copy: string
  color: string
}

export type WhiteLabelAppInfo = {
  identifier: string
  display_name: string
  app_store_url: string
  google_play_url: string
}

export type OrgCobrandingResponse = {
  landing: {
    splash: null | LandingSlide
    slides: null | LandingSlide[]
  }
  customizations: {
    css: string | null
  }
}

export type KycConfig = {
  reference_id: string
  standard: {
    template_id: string
    prefill: { [key: string]: any }
  }
  additional_documentation: {
    template_id: string
    prefill: { [key: string]: any }
  }
}

export type KycConfigResponse = Response<KycConfig>

export type Ach = {
  account_number: string
  routing_number: string
}

export type Balance = {
  currency_code: string
  current_balance: number
  available_balance: number
}

export type BillingAddress = {
  city: string
  state: string
  zip: string
  country?: string
  first_name: string
  last_name: string
  address_1: string
  address_2?: string
}

export type CardState = 'UNACTIVATED' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED'
export type CardType = 'BUSINESS' | 'CONSUMER'
export type Card = {
  id: string
  token: string
  user_token: string
  last_four: string
  state: CardState
  pin_set: boolean
  shipping_address?: BillingAddress
  fulfillment_status: string
  card_product: string
  physical: boolean
  created_time: string
  expiration: string
  bin: string
  instrument_type: string
  program_type: CardType
}

export type UserDetails = {
  id: number
  first_name: string
  last_name: string
  name: string
  email: string
  phone_number: string
}

export type UserDetailsResponse = Response<UserDetails>

export type SsoValidation = {
  action: 'signup' | 'login'
  employee_id: string
  first_name: string
  is_manager?: boolean
  last_name: string
  location_id: string
  org_name: string
  user_id: number // 0 denotes null
}

export type SsoValidationResponse = Response<SsoValidation>

export type SsoSignInValidation = {
  token: string
  userId: string
}

export type SsoSignInValidationResponse = Response<SsoSignInValidation>

export type AccountReservationDetails = {
  email: string
  first_name: string
  last_name: string
  organization_id: number
  phone: string
}

export type AccountReservationDetailsResponse =
  Response<AccountReservationDetails>

export type RosterMatch = {
  roster_match: boolean
  already_linked: boolean
}

export type RosterMatchResponse = Response<RosterMatch>

export type OrgSettings = { [key: string]: any }

export type OrgSettingsResponse = Response<OrgSettings>

export type SSOConfig = {
  idp_sso_url: string
  display_in_search: boolean
  input1_label?: string
  input2_label?: string
  show_ewa_copy: boolean
  sign_up_tip_copy?: string
  hosted_login: boolean
  display_name?: string
  logo_url?: string
  enable_paycard: boolean
}

export type EnterLastFourOnCard = {
  user_token: string
  organization_id: string
}

export type EnterLastFourOnCardResponse = Response<EnterLastFourOnCard>

export type SingleSignOnMapping = {
  id: number
  organization_id: number
  single_sign_on_id: number
}

export type SSOData = SSOConfig & {
  id: number
  identifier: string
  single_sign_on_organization_mapping_list: SingleSignOnMapping[]
}

export type SSOResponse = Response<SSOData>

export enum ACCOUNT_TYPE {
  PAYCARD = 'PAYCARD',
}

export type ValidateSSORequest = {
  code: string
  user_id: string
  employee_id: string
  location_id: string
  org_name: string
}

export type ClaimLimitedAccountRequest = {
  account_reference_token: string
  ssn_last_four: string
  debug_fail_kyc_inquiry: boolean
}

export type ValidateAccessCodeRequest = {
  access_code: string
}

export type GetCodeRequest = {
  phone: string
}

export type SignUpGetCodeRequest = {
  phone_number: string
}

export type AcceptEndUserTermsRequest = {
  organization_id: string
  accepted: boolean
}

export type PostAccessCodeRequest = {
  new_access_code: string
}

export type LinkKycRecordRequest = {
  kyc_reference_id: string
  ssn_last_four: string
}

export type MarqetaOneTimeToken = {
  token: string
}

export type MarqetaOneTimeTokenResponse = Response<MarqetaOneTimeToken>

export type ValidateLoginCodeRequest = {
  phone: string
  code: string
}

export type ValidateLoginCode = {
  token: string
  userId: string
}

export type ValidateLoginCodeResponse = Response<ValidateLoginCode>

export type ShippingTime = {
  initial: {
    shipping_time_business_day_range: { end: number; start: number }
  }
}

export type ShippingTimeResponse = Response<ShippingTime>

export type SignUpUserRequest = {
  account_type: string
  email: string
  employee_id: string
  first_name: string
  last_name: string
  phone: string
  phone_validation_code: string
  terms: Terms[]
  organization_id?: number
  locations?: string[]
}

export type SignUpUser = {
  user_id: string
}

export type SignUpUserResponse = Response<SignUpUser>

export type Terms = {
  name: string
  version: string
}

export type TermsDetails = Terms & {
  display_name: string
  display_inline?: boolean
  url: string
}

export type GetAccessCodeResponse = Response<{
  user_id: string
  has_access_code: 'true' | 'false'
}>

export type CardProgramResponse = {
  type: 'CONSUMER' | 'BUSINESS'
}
